import type { Team, IdLabelPair } from './types';

function compareStrings(a: IdLabelPair, b: IdLabelPair): number {
	return a.label.localeCompare(b.label);
}

/**
 * Returns true if the given search term is a non-empty string that is not just whitespace.
 *
 * @param term the search term to validate
 * @returns true if the search term is valid, false otherwise
 */
export function isValidSearchTerm(term?: string): boolean {
	return typeof term === 'string' && term.trim() !== '';
}

/**
 *
 * @param teams
 * @returns
 */
export function getAllTeams(teams?: Team[]): IdLabelPair[] {
	if (!teams) {
		return [];
	}

	const teamsList = [];

	for (const team of teams) {
		if (team?.name) {
			teamsList.push({
				id: team.id,
				label: team.name
			});
		}
	}

	return teamsList.sort(compareStrings);
}

/**
 * Returns a sorted array of all driver names from the given array of teams.
 * If a team does not have drivers or if a driver does not have a display name, they are skipped.
 *
 * @param teams an array of Team objects
 * @returns a sorted array of driver names
 */
export function getAllDrivers(teams?: Team[]): string[] {
	if (!teams) {
		return [];
	}

	const driversList = [];

	for (const team of teams) {
		const drivers = team?.players?.items ?? [];

		for (const driver of drivers) {
			if (driver?.displayName) {
				driversList.push(driver.displayName);
			}
		}
	}

	return driversList.sort();
}

/**
 * Returns an array containing the team name that have a driver with the given name.
 * If the driver name is not valid, returns an array of all team names.
 * If a team does not have drivers or if a driver does not have a display name, they are skipped.
 * If a team has the driver, but does not have a name, it is skipped.
 *
 * @param teams the array of Team objects to filter
 * @param driverName the name of the driver to filter by
 * @returns an array of team names
 */
export function filterTeamByDriver(teams?: Team[], driverName?: string): IdLabelPair[] {
	const teamsList = teams ?? [];

	if (!isValidSearchTerm(driverName)) {
		return getAllTeams(teamsList);
	}

	for (const team of teamsList) {
		const drivers = team?.players?.items ?? [];

		const filteredDrivers = drivers
			// transforms the array of drivers into an array of strings (i.e. driver names)
			.map((driver) => driver?.displayName)
			// filters out any driver that has an invalid display name (i.e. null, undefined, empty string)
			.filter((name) => Boolean(name));

		if (filteredDrivers.some((name) => name === driverName)) {
			if (team?.name) {
				return [
					{
						id: team.id,
						label: team.name
					}
				];
			}

			return getAllTeams(teamsList);
		}
	}

	return getAllTeams(teamsList);
}

/**
 * Returns an array containing the driver names that are in the team with the given ID.
 * If the team ID is not valid (empty string, or a non-string type), returns an array of all driver names.
 * If a team does not have drivers or if a driver does not have a display name, they are skipped.
 * If a team does not have a name, it is skipped.
 *
 * @param teams the array of Team objects to filter
 * @param teamId the ID of the team to filter by
 * @returns an array of driver names
 */
export function filterDriversByTeamId(teams?: Team[], teamId?: string): string[] {
	const teamsList = teams ?? [];

	if (!isValidSearchTerm(teamId)) {
		return getAllDrivers(teamsList);
	}

	const team = teamsList.find((team) => team?.id === teamId);

	return team
		? (team?.players?.items ?? [])
				// transforms the array of drivers into an array of strings (i.e. driver names)
				.map((driver) => driver?.displayName)
				// filters out any driver that has an invalid display name (i.e. null, undefined, empty string)
				.filter((driverName) => Boolean(driverName))
		: getAllDrivers(teamsList);
}
